import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, ArrowRight } from "lucide-react";
import api from "@/lib/api";

function fmtDate(d) {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function displayDate(d) {
  if (!d) return "Pick a date";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Quick booking widget for the homepage hero.
 * Tabs = "All vehicles" + one tab per vehicle in the fleet.
 * On submit: routes to /booking if a specific vehicle is chosen, otherwise /vehicles.
 */
export default function QuickBook() {
  const [vehicles, setVehicles] = useState([]);
  const [activeSlug, setActiveSlug] = useState("all");
  const [pickup, setPickup] = useState(null);
  const [ret, setRet] = useState(null);
  const [openPickup, setOpenPickup] = useState(false);
  const [openReturn, setOpenReturn] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const canSubmit = pickup && ret && ret > pickup;

  const submit = () => {
    if (!canSubmit) return;
    const q = new URLSearchParams({ start: fmtDate(pickup), end: fmtDate(ret) });
    if (activeSlug === "all") {
      nav(`/vehicles?${q.toString()}`);
    } else {
      q.set("slug", activeSlug);
      nav(`/booking?${q.toString()}`);
    }
  };

  const tabs = [{ slug: "all", label: "All vehicles" }, ...vehicles.map((v) => ({ slug: v.slug, label: v.name.replace("Ford ", "") }))];

  return (
    <div className="mt-8 rounded-2xl bg-white/95 backdrop-blur border border-gray-200 shadow-xl overflow-hidden max-w-4xl" data-testid="quick-book">
      {/* Tabs */}
      <div className="flex flex-wrap gap-1 px-3 pt-3 border-b border-gray-100 bg-gray-50/60">
        {tabs.map((t) => (
          <button
            key={t.slug}
            type="button"
            onClick={() => setActiveSlug(t.slug)}
            data-testid={`qb-tab-${t.slug}`}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${activeSlug === t.slug ? "bg-white text-[#E2AB20] border border-gray-200 border-b-white -mb-px" : "text-[#010F27] hover:text-[#E2AB20]"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Fields */}
      <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div>
          <label className="text-[11px] uppercase tracking-widest font-semibold text-[#4B5B76]">Pickup date</label>
          <Popover open={openPickup} onOpenChange={setOpenPickup}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="qb-pickup"
                className="mt-1 w-full flex items-center gap-2 h-12 px-4 border border-gray-300 rounded-lg text-left text-sm hover:border-[#E2AB20] transition-colors bg-white"
              >
                <CalendarIcon size={16} className="text-[#E2AB20]" />
                <span className={pickup ? "text-[#010F27] font-semibold" : "text-[#4B5B76]"}>{displayDate(pickup)}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 w-auto">
              <Calendar
                mode="single"
                selected={pickup}
                onSelect={(d) => { setPickup(d); setOpenPickup(false); if (ret && d && ret <= d) setRet(null); }}
                disabled={{ before: new Date() }}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest font-semibold text-[#4B5B76]">Return date</label>
          <Popover open={openReturn} onOpenChange={setOpenReturn}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="qb-return"
                className="mt-1 w-full flex items-center gap-2 h-12 px-4 border border-gray-300 rounded-lg text-left text-sm hover:border-[#E2AB20] transition-colors bg-white"
              >
                <CalendarIcon size={16} className="text-[#E2AB20]" />
                <span className={ret ? "text-[#010F27] font-semibold" : "text-[#4B5B76]"}>{displayDate(ret)}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 w-auto">
              <Calendar
                mode="single"
                selected={ret}
                onSelect={(d) => { setRet(d); setOpenReturn(false); }}
                disabled={{ before: pickup ? new Date(pickup.getTime() + 24 * 3600 * 1000) : new Date() }}
              />
            </PopoverContent>
          </Popover>
        </div>

        <Button
          onClick={submit}
          disabled={!canSubmit}
          data-testid="qb-submit"
          className="h-12 px-6 bg-[#E2AB20] hover:bg-[#D89B08] text-[#010F27] text-base font-bold disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Find available vehicles <ArrowRight size={18} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}
